import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { NavigationState, NavTab, ScopeFilter, EditingResource } from '@/types/navigation';

interface NavigationContextType {
  navigation: NavigationState;
  navigateToGlobal: () => void;
  navigateToPersonal: () => void;
  navigateToProject: (projectId: string, isPersonal?: boolean) => void;
  navigateToTab: (tab: NavTab) => void;
  navigateToAdmin: () => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  configPanelOpen: boolean;
  setConfigPanelOpen: (open: boolean) => void;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  activeAgentId: string | null;
  setActiveAgentId: (id: string | null) => void;
  skipNextConversationReset: React.MutableRefObject<boolean>;
  scopeFilter: ScopeFilter;
  setScopeFilter: (filter: ScopeFilter) => void;
  searchPrefill: string;
  setSearchPrefill: (term: string) => void;
  scrollToMessageId: string | null;
  setScrollToMessageId: (id: string | null) => void;
  conversationHasMessages: boolean;
  setConversationHasMessages: (has: boolean) => void;
  editingResource: EditingResource | null;
  setEditingResource: (resource: EditingResource | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [navigation, setNavigation] = useState<NavigationState>({
    tab: 'chat',
    projectId: undefined,
    isPersonal: true,
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [configPanelOpen, setConfigPanelOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const skipNextConversationReset = useRef(false);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('personal');
  const [searchPrefill, setSearchPrefill] = useState('');
  const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null);
  const [conversationHasMessages, setConversationHasMessages] = useState(false);
  const [editingResource, setEditingResource] = useState<EditingResource | null>(null);

  // Auto-sync scopeFilter when navigation context changes
  useEffect(() => {
    if (navigation.projectId) {
      setScopeFilter(`project:${navigation.projectId}`);
    } else if (navigation.isPersonal) {
      setScopeFilter('personal');
    } else {
      setScopeFilter('global');
    }
  }, [navigation.isPersonal, navigation.projectId]);

  useEffect(() => {
    if (skipNextConversationReset.current) {
      skipNextConversationReset.current = false;
      return;
    }
    setActiveConversationId(null);
  }, [navigation.projectId, navigation.isPersonal]);

  const navigateToGlobal = useCallback(() => {
    setEditingResource(null);
    setNavigation(prev => ({
      ...prev,
      projectId: undefined,
      isPersonal: false,
    }));
  }, []);

  const navigateToPersonal = useCallback(() => {
    setEditingResource(null);
    setNavigation(prev => ({
      ...prev,
      projectId: undefined,
      isPersonal: true,
    }));
  }, []);

  const navigateToProject = useCallback((projectId: string, isPersonal = false) => {
    setEditingResource(null);
    setNavigation(prev => ({
      ...prev,
      projectId,
      isPersonal,
    }));
  }, []);

  const navigateToTab = useCallback((tab: NavTab) => {
    setEditingResource(null);
    setNavigation(prev => ({
      ...prev,
      tab,
    }));
  }, []);

  const navigateToAdmin = useCallback(() => {
    setEditingResource(null);
    setNavigation({ tab: 'admin', projectId: undefined, isPersonal: false });
  }, []);

  return (
    <NavigationContext.Provider
      value={{
        navigation,
        navigateToGlobal,
        navigateToPersonal,
        navigateToProject,
        navigateToTab,
        navigateToAdmin,
        sidebarCollapsed,
        setSidebarCollapsed,
        configPanelOpen,
        setConfigPanelOpen,
        activeConversationId,
        setActiveConversationId,
        activeAgentId,
        setActiveAgentId,
        skipNextConversationReset,
        scopeFilter,
        setScopeFilter,
        searchPrefill,
        setSearchPrefill,
        scrollToMessageId,
        setScrollToMessageId,
        conversationHasMessages,
        setConversationHasMessages,
        editingResource,
        setEditingResource,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};
